import { useEffect, useState } from "react";
import { useSession } from "@/auth/session";
import { getDteUrl } from "@/lib/runtime-config";
import {
  getSettingsSnapshot,
  updateBillingSettings,
  updateInvoiceIntegrationSettings,
  updateOrganizationSettings,
} from "@/lib/api";

export default function SistemaTab() {
  const { session } = useSession();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    nombreApr: "",
    rut: "",
    giro: "",
    region: "",
    comuna: "",
    direccion: "",
    telefono: "",
    email: "",
    periodoFacturacion: "monthly",
    diaCorte: "25",
    diaVencimiento: "10",
    mensajeCierre: "Estimado(a) cliente, su cuenta corresponde al cierre del período vigente.",
    contractNumberSeed: "1",
    contractNumberStep: "1",
    morosidadMonths: "2",
  });

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (!session) return;
      setLoading(true);
      setError("");
      try {
        const snapshot = await getSettingsSnapshot(session);
        if (cancelled) return;
        const extraJson = (snapshot.invoiceIntegration?.extraJson ?? {}) as Record<string, unknown>;
        setForm({
          nombreApr: snapshot.organization?.companyName ?? "",
          rut: snapshot.organization?.companyRut ?? "",
          giro: snapshot.organization?.giro ?? "",
          region: snapshot.organization?.region ?? "",
          comuna: snapshot.organization?.comuna ?? "",
          direccion: snapshot.organization?.address ?? "",
          telefono: snapshot.organization?.phone ?? "",
          email: snapshot.organization?.email ?? "",
          periodoFacturacion: snapshot.billing?.billingFrequency ?? "monthly",
          diaCorte: String(snapshot.billing?.cutoffDay ?? 25),
          diaVencimiento: String(snapshot.billing?.dueDay ?? 10),
          contractNumberSeed: String(snapshot.organization?.contractNumberSeed ?? 1),
          contractNumberStep: String(snapshot.organization?.contractNumberStep ?? 1),
          morosidadMonths: String(snapshot.organization?.morosidadMonths ?? 2),
          mensajeCierre:
            typeof extraJson.periodClosureMessage === "string"
              ? (extraJson.periodClosureMessage as string)
              : "Estimado(a) cliente, su cuenta corresponde al cierre del período vigente.",
        });
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "No se pudo cargar configuración");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [session]);

  const handleChange = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    if (!session) return;
    setSaving(true);
    setError("");
    try {
      await updateOrganizationSettings(session, {
        companyName: form.nombreApr,
        companyRut: form.rut,
        giro: form.giro,
        region: form.region,
        comuna: form.comuna,
        address: form.direccion,
        phone: form.telefono,
        email: form.email,
        contractNumberSeed: Number(form.contractNumberSeed) || 1,
        contractNumberStep: Number(form.contractNumberStep) || 1,
        morosidadMonths: Number(form.morosidadMonths) || 2,
      });
      await updateBillingSettings(session, {
        billingFrequency: form.periodoFacturacion,
        cutoffDay: Number(form.diaCorte),
        dueDay: Number(form.diaVencimiento),
      });
      const current = await getSettingsSnapshot(session);
      const extraJson = (current.invoiceIntegration?.extraJson ?? {}) as Record<string, unknown>;
      const dte = ((extraJson.dte as Record<string, unknown> | undefined) ?? {}) as Record<string, unknown>;
      const currentEmisor = ((dte.emisor as Record<string, unknown> | undefined) ?? {}) as Record<string, unknown>;
      const currentCompany = ((dte.company as Record<string, unknown> | undefined) ?? {}) as Record<string, unknown>;
      await updateInvoiceIntegrationSettings(session, {
        providerName: current.invoiceIntegration?.providerName ?? "dte_cloud",
        endpoint: current.invoiceIntegration?.endpoint ?? getDteUrl(),
        status: current.invoiceIntegration?.status ?? "active",
        extraJson: {
          ...extraJson,
          dte: {
            ...dte,
            emisor: {
              ...currentEmisor,
              rut: form.rut,
              razon: form.nombreApr,
              giro: form.giro,
              direccion: form.direccion,
              comuna: form.comuna,
              ciudad: form.comuna,
              email: form.email,
            },
            company: {
              ...currentCompany,
              rut: form.rut,
            },
          },
          periodClosureMessage: form.mensajeCierre,
        },
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudieron guardar cambios");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      {error ? <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{error}</div> : null}
      {loading ? <div className="text-sm text-gray-500">Cargando configuración...</div> : null}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <h2 className="text-sm font-semibold text-gray-800 mb-4">Información del sistema</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Nombre APR</label>
            <input value={form.nombreApr} onChange={(e) => handleChange("nombreApr", e.target.value)} placeholder="Nombre APR" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">RUT</label>
            <input value={form.rut} onChange={(e) => handleChange("rut", e.target.value)} placeholder="RUT" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Giro</label>
            <input value={form.giro} onChange={(e) => handleChange("giro", e.target.value)} placeholder="Giro" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Región</label>
            <input value={form.region} onChange={(e) => handleChange("region", e.target.value)} placeholder="Región" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Comuna</label>
            <input value={form.comuna} onChange={(e) => handleChange("comuna", e.target.value)} placeholder="Comuna" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Dirección</label>
            <input value={form.direccion} onChange={(e) => handleChange("direccion", e.target.value)} placeholder="Dirección" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Teléfono</label>
            <input value={form.telefono} onChange={(e) => handleChange("telefono", e.target.value)} placeholder="Teléfono" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Email</label>
            <input value={form.email} onChange={(e) => handleChange("email", e.target.value)} placeholder="Email" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Período facturación</label>
            <select value={form.periodoFacturacion} onChange={(e) => handleChange("periodoFacturacion", e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg">
              <option value="monthly">Mensual</option>
              <option value="bimonthly">Bimestral</option>
              <option value="quarterly">Trimestral</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Día corte</label>
            <input value={form.diaCorte} onChange={(e) => handleChange("diaCorte", e.target.value)} placeholder="Día corte" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Día vencimiento</label>
            <input value={form.diaVencimiento} onChange={(e) => handleChange("diaVencimiento", e.target.value)} placeholder="Día vencimiento" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          </div>
          <div className="col-span-2">
            <div className="rounded-xl border border-gray-100 bg-gray-50 p-4 space-y-3">
              <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Correlativo de contratos</div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Número inicial (semilla)</label>
                  <input
                    type="number"
                    min={1}
                    value={form.contractNumberSeed}
                    onChange={(e) => handleChange("contractNumberSeed", e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
                  />
                  <p className="text-xs text-gray-400 mt-1">Primer número si no hay contratos previos</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Salto entre contratos</label>
                  <select
                    value={form.contractNumberStep}
                    onChange={(e) => handleChange("contractNumberStep", e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
                  >
                    <option value="1">1 en 1 (1, 2, 3...)</option>
                    <option value="10">10 en 10 (10, 20, 30...)</option>
                    <option value="100">100 en 100 (100, 200, 300...)</option>
                  </select>
                  <p className="text-xs text-gray-400 mt-1">Incremento al crear cada contrato nuevo</p>
                </div>
              </div>
            </div>
          </div>

          <div className="col-span-2">
            <div className="rounded-xl border border-amber-100 bg-amber-50 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <i className="ri-error-warning-line text-amber-500 text-sm" />
                <div className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Parámetro de morosidad</div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Meses de atraso para morosidad</label>
                  <select
                    value={form.morosidadMonths}
                    onChange={(e) => handleChange("morosidadMonths", e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-amber-200 rounded-lg bg-white"
                  >
                    <option value="1">1 mes</option>
                    <option value="2">2 meses</option>
                    <option value="3">3 meses</option>
                    <option value="4">4 meses</option>
                    <option value="6">6 meses</option>
                  </select>
                  <p className="text-xs text-amber-600 mt-1">Contratos con más de este período sin pagar se consideran morosos y aplican para corte de servicio</p>
                </div>
              </div>
            </div>
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Mensaje de cierre por defecto</label>
            <textarea
              value={form.mensajeCierre}
              onChange={(e) => handleChange("mensajeCierre", e.target.value)}
              rows={4}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
              placeholder="Mensaje por defecto para cierre de periodo en factura"
            />
          </div>
        </div>
      </div>
      <div className="flex items-center justify-end gap-3">
        {saved ? <div className="text-emerald-600 text-sm">Cambios guardados correctamente</div> : null}
        <button
          onClick={() => void handleSave()}
          disabled={saving || loading}
          className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-60"
        >
          {saving ? "Guardando..." : "Guardar cambios"}
        </button>
      </div>
    </div>
  );
}
